import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Send, Lock, MessagesSquare, AlertTriangle, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPost, apiPatch } from "@/lib/api";
import { TICKET_STATUS_VARIANT, PRIORITY_VARIANT } from "@/lib/rbac";
import { slaInfo, formatRel } from "@/lib/sla";
import pt from "@/lib/i18n";

const TicketDetailPage = () => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [comment, setComment] = useState("");
    const [internal, setInternal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const d = await apiGet(`/tickets/${ticketId}`);
            setData(d);
        } catch (e) { setData(null); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId]);

    const post = async (e) => {
        e.preventDefault();
        if (!comment.trim()) return;
        setSubmitting(true);
        try {
            await apiPost(`/tickets/${ticketId}/comments`, { body: comment.trim(), is_internal: internal });
            setComment("");
            load();
        } catch (e) { /* ignore */ }
        finally { setSubmitting(false); }
    };

    const updateStatus = async (status) => {
        setBusy(true);
        try {
            await apiPatch(`/tickets/${ticketId}`, { status });
            load();
        } catch (e) { /* ignore */ }
        finally { setBusy(false); }
    };

    if (!data) {
        return <div><Skeleton height="40px" width="320px" /><div className="mt-6"><Skeleton height="240px" /></div></div>;
    }

    const { ticket, customer, assignee, creator, comments } = data;
    const sla = slaInfo(ticket.sla_deadline);
    const STATUS_NEXT = {
        aberto: [{ to: "em_progresso", label: "Iniciar trabalho", variant: "copper" }],
        em_progresso: [
            { to: "pendente_cliente", label: "Aguardar cliente", variant: "secondary" },
            { to: "resolvido", label: "Marcar resolvido", variant: "copper" },
        ],
        pendente_cliente: [
            { to: "em_progresso", label: "Retomar trabalho", variant: "secondary" },
            { to: "resolvido", label: "Marcar resolvido", variant: "copper" },
        ],
        resolvido: [
            { to: "fechado", label: "Fechar ticket", variant: "secondary" },
            { to: "em_progresso", label: "Reabrir", variant: "secondary" },
        ],
        fechado: [{ to: "aberto", label: "Reabrir", variant: "secondary" }],
    };
    const actions = STATUS_NEXT[ticket.status] || [];

    return (
        <div data-testid="ticket-detail-root">
            <button type="button" onClick={() => navigate("/tickets")} className="inline-flex items-center gap-1.5 text-sm text-[color:var(--slate-mid)] hover:text-[color:var(--copper-dark)] mb-4" data-testid="back-to-tickets">
                <ArrowLeft size={14} /> {pt.tickets.backToList}
            </button>

            <PageHeader
                title={<span className="font-mono">{ticket.ticket_number}</span>}
                subtitle={ticket.subject}
                right={
                    <div className="flex items-center gap-2 flex-wrap">
                        <NxBadge variant={TICKET_STATUS_VARIANT[ticket.status]}>{pt.tickets.statuses[ticket.status]}</NxBadge>
                        <NxBadge variant={PRIORITY_VARIANT[ticket.priority]}>{pt.tickets[`priority_${ticket.priority}`]}</NxBadge>
                        <NxBadge variant={sla.variant}><Clock size={12} /> {sla.label}</NxBadge>
                    </div>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
                {/* Left column — description + comments */}
                <div className="space-y-4">
                    <GlassCard padding="p-6" testId="ticket-description">
                        <h3 className="font-display text-lg text-[color:var(--slate-deep)] mb-2">{pt.common.description}</h3>
                        <p className="font-body text-sm text-[color:var(--slate-mid)] whitespace-pre-line leading-relaxed">{ticket.description}</p>
                        <div className="mt-4 pt-4 border-t border-[color:var(--glass-border)] flex items-center gap-4 text-xs">
                            <span className="text-[color:var(--slate-mid)]">Criado por <span className="text-[color:var(--slate-deep)] font-medium">{creator?.full_name || "—"}</span></span>
                            <span className="font-mono text-[color:var(--slate-light)]">{formatRel(ticket.created_at)}</span>
                        </div>
                    </GlassCard>

                    <GlassCard padding="p-6" testId="comments-thread">
                        <h3 className="font-display text-lg text-[color:var(--slate-deep)] mb-4">
                            <MessagesSquare size={16} className="inline mr-2 text-[color:var(--copper)]" />
                            Comentários ({comments.length})
                        </h3>
                        <ul className="space-y-3 mb-5">
                            {comments.length === 0 && (
                                <li className="text-sm text-[color:var(--slate-mid)] text-center py-6">{pt.common.noComments}</li>
                            )}
                            {comments.map((c) => (
                                <li
                                    key={c.id}
                                    className="rounded-xl p-3.5"
                                    style={{
                                        background: c.is_internal ? "var(--copper-subtle)" : "var(--bg-surface)",
                                        border: c.is_internal ? "1px dashed var(--copper-glow)" : "1px solid var(--glass-border-subtle)",
                                    }}
                                    data-testid={`comment-${c.id}`}
                                >
                                    <div className="flex items-center gap-2.5 mb-1.5">
                                        <NxAvatar name={c.author?.full_name} size="sm" />
                                        <div className="flex-1">
                                            <span className="font-body text-sm font-medium text-[color:var(--slate-deep)]">{c.author?.full_name || "—"}</span>
                                            <span className="font-mono text-[10px] text-[color:var(--slate-light)] ml-2">{formatRel(c.created_at)}</span>
                                        </div>
                                        {c.is_internal && <NxBadge variant="warning"><Lock size={10} /> Interna</NxBadge>}
                                    </div>
                                    <p className="font-body text-sm text-[color:var(--slate-deep)] leading-relaxed">{c.body}</p>
                                </li>
                            ))}
                        </ul>

                        <form onSubmit={post}>
                            <textarea
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder={pt.tickets.addComment}
                                className="glass-input w-full resize-none"
                                data-testid="comment-input"
                            />
                            <div className="mt-3 flex items-center justify-between">
                                <label className="inline-flex items-center gap-2 text-sm text-[color:var(--slate-mid)] cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={internal}
                                        onChange={(e) => setInternal(e.target.checked)}
                                        className="accent-[color:var(--copper)]"
                                        data-testid="comment-internal-toggle"
                                    />
                                    {internal ? pt.tickets.internalNote : pt.tickets.publicReply}
                                </label>
                                <NxButton
                                    variant="copper"
                                    type="submit"
                                    icon={<Send size={14} />}
                                    loading={submitting}
                                    disabled={!comment.trim()}
                                    testId="comment-submit"
                                >
                                    {pt.tickets.publishComment}
                                </NxButton>
                            </div>
                        </form>
                    </GlassCard>
                </div>

                {/* Right — meta + actions */}
                <div className="space-y-4">
                    <GlassCard padding="p-5" testId="ticket-meta">
                        <h4 className="font-display text-base text-[color:var(--slate-deep)] mb-3">{pt.common.info}</h4>
                        <dl className="space-y-2 text-sm">
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">Cliente</dt><dd className="text-[color:var(--slate-deep)] text-right">{customer?.full_name || "—"}</dd></div>
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">Categoria</dt><dd><NxBadge variant="neutral">{pt.tickets.categories[ticket.category]}</NxBadge></dd></div>
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">{pt.common.assigned}</dt><dd className="text-[color:var(--slate-deep)]">{assignee?.full_name || "—"}</dd></div>
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">SLA</dt><dd className="font-mono text-[color:var(--slate-deep)]">{ticket.sla_hours}h</dd></div>
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">Restante</dt><dd><NxBadge variant={sla.variant}>{sla.label}</NxBadge></dd></div>
                        </dl>
                    </GlassCard>

                    {actions.length > 0 && (
                        <GlassCard padding="p-5">
                            <h4 className="font-display text-base text-[color:var(--slate-deep)] mb-3">{pt.common.actions}</h4>
                            <div className="flex flex-col gap-2">
                                {actions.map((a) => (
                                    <NxButton key={a.to} variant={a.variant} loading={busy} onClick={() => updateStatus(a.to)} testId={`ticket-action-${a.to}`}>
                                        {a.label}
                                    </NxButton>
                                ))}
                            </div>
                        </GlassCard>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TicketDetailPage;
